// src/components/OnboardingFlow.js - New client onboarding experience
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from './Icon';
import Button from './Button';

const OnboardingFlow = ({ onLogin }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState('welcome'); // welcome, initial-question, auth-prompt, assessment, therapist-matching, booking
  const [initialQuestion, setInitialQuestion] = useState('');
  const [assessmentData, setAssessmentData] = useState({
    situation: '',
    family: '',
    work: '',
    religion: '',
    abuse: '',
    addiction: '',
    anxiety: '',
    other_concerns: ''
  });
  const [matchedTherapists, setMatchedTherapists] = useState([]);
  const [selectedTherapist, setSelectedTherapist] = useState(null);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Check if user is returning from auth and should continue to assessment
  useEffect(() => {
    if (location.state?.step === 'assessment') {
      const savedData = getOnboardingData();
      if (savedData && savedData.initialQuestion) {
        setInitialQuestion(savedData.initialQuestion);
        setCurrentStep('assessment');
      }
    }
  }, [location.state]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [currentStep]);

  // Save onboarding data to localStorage for persistence
  const saveOnboardingData = (data) => {
    localStorage.setItem('onboarding_data', JSON.stringify(data));
  };

  const getOnboardingData = () => {
    const saved = localStorage.getItem('onboarding_data');
    return saved ? JSON.parse(saved) : null;
  };

  const handleInitialQuestionSubmit = () => {
    if (!initialQuestion.trim()) return;
    
    // Save the initial question
    const onboardingData = { 
      initialQuestion: initialQuestion,
      timestamp: new Date().toISOString()
    };
    saveOnboardingData(onboardingData);
    
    setCurrentStep('auth-prompt');
  };

  const handleLoginRedirect = () => {
    // Redirect to login with return path
    navigate('/login', { 
      state: { 
        returnTo: '/onboarding',
        step: 'assessment'
      }
    });
  };

  const handleRegisterRedirect = () => {
    // Redirect to register with return path
    navigate('/register', { 
      state: { 
        returnTo: '/onboarding',
        step: 'assessment'
      }
    });
  };

  const handleAssessmentSubmit = async () => {
    setLoading(true);
    try {
      // Save assessment data
      const savedData = getOnboardingData() || {};
      const completeData = {
        ...savedData,
        assessment: assessmentData,
        assessmentCompleted: new Date().toISOString()
      };
      saveOnboardingData(completeData);

      // Get auth token if user is logged in
      const token = localStorage.getItem('therapy_auth_token');
      
      if (token) {
        // Call backend API for therapist matching
        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        
        const response = await fetch(`${API_BASE}/api/onboarding/match-therapists`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            initial_question: initialQuestion,
            assessment: assessmentData
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          setMatchedTherapists(data.matched_therapists || []);
        } else {
          console.error('Failed to match therapists, using mock data');
          await simulateTherapistMatching();
        }
      } else {
        // User not logged in, use mock data
        await simulateTherapistMatching();
      }
      
      setCurrentStep('therapist-matching');
    } catch (error) {
      console.error('Assessment submission failed:', error);
      // Fallback to mock data
      await simulateTherapistMatching();
      setCurrentStep('therapist-matching');
    } finally {
      setLoading(false);
    }
  };

  const simulateTherapistMatching = async () => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Mock therapist data based on assessment
    const mockTherapists = [
      {
        id: 1,
        name: 'Dr. Anna Lindgren',
        specialties: ['Ångest', 'Depression', 'Kognitiv beteendeterapi'],
        image: '/api/placeholder/150/150',
        bio: 'Legitimerad psykolog med över 10 års erfarenhet inom ångest och depression. Specialiserad på KBT och mindfulness.',
        availability: 'Nästa vecka',
        matchScore: 95
      },
      {
        id: 2,
        name: 'Dr. Erik Johansson',
        specialties: ['Relationer', 'Stress', 'Parterapi'],
        image: '/api/placeholder/150/150',
        bio: 'Familjeterapeut med fokus på relationer och kommunikation. Hjälper par och individer att bygga starkare band.',
        availability: 'Inom 2 veckor',
        matchScore: 88
      },
      {
        id: 3,
        name: 'Dr. Maria Andersson',
        specialties: ['Trauma', 'EMDR', 'Posttraumatisk stress'],
        image: '/api/placeholder/150/150',
        bio: 'Specialist inom traumabehandling med EMDR-certifiering. Hjälper klienter att bearbeta svåra upplevelser.',
        availability: 'Nästa vecka',
        matchScore: 92
      }
    ];

    setMatchedTherapists(mockTherapists);
  };

  const handleTherapistSelect = async (therapist) => {
    setSelectedTherapist(therapist);
    
    // Save selection to backend if user is logged in
    const token = localStorage.getItem('therapy_auth_token');
    
    if (token) {
      try {
        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        
        const response = await fetch(`${API_BASE}/api/onboarding/select-therapist`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            therapist_id: therapist.id,
            booking_preference: "pending"
          })
        });
        
        if (response.ok) {
          console.log('Therapist selection saved to backend');
        } else {
          console.error('Failed to save therapist selection');
        }
      } catch (error) {
        console.error('Error saving therapist selection:', error);
      }
    }
    
    setCurrentStep('booking');
  };

  const handleSkipBooking = () => {
    // Save selection and redirect to AI chat
    const savedData = getOnboardingData() || {};
    const completeData = {
      ...savedData,
      selectedTherapist: selectedTherapist,
      bookingSkipped: true,
      completedAt: new Date().toISOString()
    };
    saveOnboardingData(completeData);
    
    navigate('/client/chat', { 
      state: { 
        onboardingCompleted: true,
        initialQuestion: initialQuestion
      }
    });
  };

  const renderWelcomeStep = () => (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-red-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="message-circle-heart" className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Välkommen till Your Therapist</h1>
          <p className="text-lg text-gray-600">Vi är här för att hjälpa dig hitta rätt stöd för dina behov</p>
        </div>

        <div className="bg-gradient-to-r from-red-50 to-red-100 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Vad kan vi hjälpa dig med idag?</h2>
          <textarea
            value={initialQuestion}
            onChange={(e) => setInitialQuestion(e.target.value)}
            placeholder="Berätta för oss vad du skulle vilja ha hjälp med... Det kan vara ångest, relationer, stress, eller något helt annat. Ju mer du delar, desto bättre kan vi matcha dig med rätt terapeut."
            className="w-full h-32 p-4 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
            maxLength={1000}
          />
          <div className="flex justify-between items-center mt-4">
            <span className="text-sm text-gray-500">
              {initialQuestion.length}/1000 tecken
            </span>
            <Button 
              onClick={handleInitialQuestionSubmit}
              disabled={!initialQuestion.trim()}
              className="bg-red-600 hover:bg-red-700"
            >
              <Icon name="arrow-right" size={16} className="mr-2" />
              Fortsätt
            </Button>
          </div>
        </div>

        <div className="text-center text-sm text-gray-500">
          <p>🔒 All information behandlas konfidentiellt och säkert</p>
        </div>
      </div>
    </div>
  );

  const renderAuthPrompt = () => (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="text-center mb-6">
          <Icon name="shield-check" size={48} className="text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Ditt meddelande är sparat!</h2>
          <p className="text-gray-600">För att fortsätta och få personlig hjälp behöver du skapa ett konto eller logga in.</p>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-gray-900 mb-2">Vad du skrev:</h3>
          <p className="text-gray-700 italic">"{initialQuestion}"</p>
        </div>

        <div className="space-y-4">
          <Button 
            onClick={handleRegisterRedirect}
            className="w-full bg-red-600 hover:bg-red-700 py-3"
          >
            <Icon name="user-plus" size={16} className="mr-2" />
            Skapa nytt konto
          </Button>
          
          <Button 
            onClick={handleLoginRedirect}
            variant="outline"
            className="w-full py-3"
          >
            <Icon name="log-in" size={16} className="mr-2" />
            Logga in
          </Button>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            Vi kommer att använda denna information för att ge dig bästa möjliga matchning
          </p>
        </div>
      </div>
    </div>
  );

  const renderAssessmentStep = () => (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Berätta mer om din situation</h2>
          <p className="text-gray-600">Detta hjälper oss att matcha dig med en terapeut som passar dina specifika behov.</p>
        </div>

        <div className="space-y-6">
          {[
            { key: 'situation', label: 'Beskriv din nuvarande situation', placeholder: 'Vad händer i ditt liv just nu som du skulle vilja ha hjälp med?' },
            { key: 'family', label: 'Familj och relationer', placeholder: 'Hur ser dina familjerelationer ut? Eventuella utmaningar?' },
            { key: 'work', label: 'Arbete och karriär', placeholder: 'Hur trivs du på jobbet? Stress eller andra arbetsrelaterade frågor?' },
            { key: 'anxiety', label: 'Ångest och oro', placeholder: 'Upplever du ångest? I vilka situationer?' },
            { key: 'religion', label: 'Religion och spiritualitet', placeholder: 'Spelar religion eller spiritualitet någon roll i ditt liv?' },
            { key: 'abuse', label: 'Trauma eller övergrepp', placeholder: 'Har du upplevt trauma som du behöver bearbeta? (valfritt att dela)' },
            { key: 'addiction', label: 'Beroende eller missbruk', placeholder: 'Finns det beroenden du vill få hjälp med?' },
            { key: 'other_concerns', label: 'Andra bekymmer', placeholder: 'Något annat du vill att din terapeut ska veta om?' }
          ].map((field) => (
            <div key={field.key}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {field.label}
              </label>
              <textarea
                value={assessmentData[field.key]}
                onChange={(e) => setAssessmentData({
                  ...assessmentData,
                  [field.key]: e.target.value
                })}
                placeholder={field.placeholder}
                className="w-full h-24 p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
              />
            </div>
          ))}
        </div>

        <div className="mt-8 flex justify-between">
          <Button 
            variant="outline"
            onClick={() => setCurrentStep('auth-prompt')}
          >
            <Icon name="arrow-left" size={16} className="mr-2" />
            Tillbaka
          </Button>
          
          <Button 
            onClick={handleAssessmentSubmit}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Analyserar...
              </>
            ) : (
              <>
                Hitta matchande terapeuter
                <Icon name="arrow-right" size={16} className="ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );

  const renderTherapistMatching = () => (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="text-center mb-8">
          <Icon name="users" size={48} className="text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Vi hittade terapeuter som passar dig!</h2>
          <p className="text-gray-600">Baserat på din information har vi matchat dig med dessa kvalificerade terapeuter.</p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {matchedTherapists.map((therapist) => (
            <div key={therapist.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
              <div className="text-center mb-4">
                <div className="w-20 h-20 bg-gray-200 rounded-full mx-auto mb-3 overflow-hidden">
                  <img 
                    src={therapist.image} 
                    alt={therapist.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80"><rect width="80" height="80" fill="%23f3f4f6"/><text x="40" y="45" text-anchor="middle" fill="%236b7280" font-size="12" font-family="Arial">${therapist.name.split(' ').map(n => n[0]).join('')}</text></svg>`;
                    }}
                  />
                </div>
                <h3 className="font-semibold text-gray-900">{therapist.name}</h3>
                <div className="flex items-center justify-center mt-1">
                  <Icon name="star" size={16} className="text-yellow-400 mr-1" />
                  <span className="text-sm text-gray-600">{therapist.matchScore}% matchning</span>
                </div>
              </div>

              <div className="mb-4">
                <div className="flex flex-wrap gap-1 mb-3">
                  {therapist.specialties.map((specialty, index) => (
                    <span key={index} className="px-2 py-1 bg-red-50 text-red-700 text-xs rounded-full">
                      {specialty}
                    </span>
                  ))}
                </div>
                <p className="text-sm text-gray-600 mb-2">{therapist.bio}</p>
                <p className="text-sm font-medium text-green-600">
                  <Icon name="calendar" size={12} className="inline mr-1" />
                  Tillgänglig: {therapist.availability}
                </p>
              </div>

              <Button 
                onClick={() => handleTherapistSelect(therapist)}
                className="w-full bg-red-600 hover:bg-red-700"
                size="sm"
              >
                Välj {therapist.name.split(' ')[1]}
              </Button>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <Button 
            variant="outline"
            onClick={() => setCurrentStep('assessment')}
          >
            <Icon name="arrow-left" size={16} className="mr-2" />
            Tillbaka till bedömning
          </Button>
        </div>
      </div>
    </div>
  );

  const renderBookingStep = () => (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="text-center mb-6">
          <Icon name="calendar-check" size={48} className="text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Du har valt {selectedTherapist?.name}</h2>
          <p className="text-gray-600">Vill du boka ett introduktionsmöte eller börja med AI-terapi först?</p>
        </div>

        {selectedTherapist && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-gray-200 rounded-full overflow-hidden">
                <img 
                  src={selectedTherapist.image} 
                  alt={selectedTherapist.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64"><rect width="64" height="64" fill="%23f3f4f6"/><text x="32" y="38" text-anchor="middle" fill="%236b7280" font-size="10" font-family="Arial">${selectedTherapist.name.split(' ').map(n => n[0]).join('')}</text></svg>`;
                  }}
                />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{selectedTherapist.name}</h3>
                <p className="text-sm text-gray-600">{selectedTherapist.specialties.join(', ')}</p>
                <p className="text-sm text-green-600 mt-1">
                  <Icon name="calendar" size={12} className="inline mr-1" />
                  {selectedTherapist.availability}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div className="border border-red-200 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2">📅 Boka introduktionsmöte</h3>
            <p className="text-sm text-gray-600 mb-3">
              Ett personligt möte för att lära känna din terapeut och diskutera dina mål (30 min, gratis).
            </p>
            <Button className="w-full bg-red-600 hover:bg-red-700">
              <Icon name="calendar-plus" size={16} className="mr-2" />
              Boka introduktionsmöte
            </Button>
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2">🤖 Börja med AI-terapi</h3>
            <p className="text-sm text-gray-600 mb-3">
              Starta direkt med AI-assisterad terapi. Du kan boka ett möte med din terapeut senare.
            </p>
            <Button 
              variant="outline"
              className="w-full"
              onClick={handleSkipBooking}
            >
              <Icon name="message-circle" size={16} className="mr-2" />
              Börja chatta nu
            </Button>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Button 
            variant="ghost"
            onClick={() => setCurrentStep('therapist-matching')}
            size="sm"
          >
            <Icon name="arrow-left" size={14} className="mr-1" />
            Välj annan terapeut
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2" onClick={() => navigate('/')} style={{cursor: 'pointer'}}>
              <div className="w-8 h-8 bg-red-800 rounded-full flex items-center justify-center">
                <Icon name="message-circle-heart" className="w-5 h-5 text-white" />
              </div>
              <span className="text-2xl font-bold text-gray-900">Your Therapist</span>
            </div>
            
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost"
                onClick={() => navigate('/login')}
                size="sm"
              >
                Logga in
              </Button>
              <Button 
                onClick={() => navigate('/register')}
                size="sm"
                className="bg-red-600 hover:bg-red-700"
              >
                Registrera
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-8 px-4">
        {currentStep === 'welcome' && renderWelcomeStep()}
        {currentStep === 'auth-prompt' && renderAuthPrompt()}
        {currentStep === 'assessment' && renderAssessmentStep()}
        {currentStep === 'therapist-matching' && renderTherapistMatching()}
        {currentStep === 'booking' && renderBookingStep()}
        <div ref={messagesEndRef} />
      </main>
    </div>
  );
};

export default OnboardingFlow;
